import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, Circle, Clock, Phone, Mail, FileText, 
  AlertTriangle, ArrowRight, Scale, Ban, Send, Euro,
  ChevronRight, PlayCircle, Pause, Flag, MessageCircle,
  Upload, CheckSquare, DollarSign
} from 'lucide-react';

// Definición completa del flujo de trabajo
const WORKFLOW_COMPLETO = [
  {
    id: 'INICIO',
    numero: 0,
    nombre: 'Registro Creado',
    descripcion: 'Alta del registro de cobro',
    icono: PlayCircle,
    color: 'gray',
    estadosRequeridos: ['PENDIENTE', 'EN_RECLAMACION', 'EXTRAJUDICIAL', 'DEMANDA_EJECUCION', 'COBRADO', 'FINALIZADO', 'CANCELADO'],
  },
  {
    id: 'RECLAMACION_1',
    numero: 1,
    nombre: '1ª Reclamación',
    descripcion: 'Primer contacto con el deudor',
    icono: Mail,
    color: 'blue',
    estadosRequeridos: ['EN_RECLAMACION', 'EXTRAJUDICIAL', 'DEMANDA_EJECUCION', 'COBRADO', 'FINALIZADO'],
    contactoRequerido: 1,
    plantilla: 'PRIMERA_RECLAMACION',
    diasEspera: 10,
    accionesDisponibles: ['email', 'whatsapp', 'telefono'],
  },
  {
    id: 'RECLAMACION_2',
    numero: 2,
    nombre: '2ª Reclamación',
    descripcion: 'Segundo aviso si no hay respuesta',
    icono: Mail,
    color: 'blue',
    estadosRequeridos: ['EN_RECLAMACION', 'EXTRAJUDICIAL', 'DEMANDA_EJECUCION', 'COBRADO', 'FINALIZADO'],
    contactoRequerido: 2,
    plantilla: 'SEGUNDA_RECLAMACION',
    diasEspera: 5,
    accionesDisponibles: ['email', 'whatsapp', 'telefono'],
  },
  {
    id: 'RECLAMACION_3',
    numero: 3,
    nombre: '3ª Reclamación',
    descripcion: 'Último aviso antes de escalar',
    icono: AlertTriangle,
    color: 'orange',
    estadosRequeridos: ['EN_RECLAMACION', 'EXTRAJUDICIAL', 'DEMANDA_EJECUCION', 'COBRADO', 'FINALIZADO'],
    contactoRequerido: 3,
    plantilla: 'TERCERA_RECLAMACION',
    diasEspera: 2,
    accionesDisponibles: ['email', 'whatsapp', 'telefono'],
  },
  {
    id: 'EXTRAJUDICIAL',
    numero: 4,
    nombre: 'Extrajudicial',
    descripcion: 'Burofax o carta certificada',
    icono: FileText,
    color: 'red',
    estadosRequeridos: ['EXTRAJUDICIAL', 'DEMANDA_EJECUCION', 'COBRADO', 'FINALIZADO'],
    estadoNecesario: 'EXTRAJUDICIAL',
    diasEspera: 15,
    accionesDisponibles: ['tramitar', 'email', 'telefono'],
  },
  {
    id: 'DEMANDA',
    numero: 5,
    nombre: 'Demanda Ejecución',
    descripcion: 'Procedimiento judicial',
    icono: Scale,
    color: 'purple',
    estadosRequeridos: ['DEMANDA_EJECUCION', 'COBRADO', 'FINALIZADO'],
    estadoNecesario: 'DEMANDA_EJECUCION',
    accionesDisponibles: ['tramitar', 'telefono'],
  },
  {
    id: 'COBRADO',
    numero: 6,
    nombre: 'Cobrado',
    descripcion: 'Deuda recuperada',
    icono: Euro,
    color: 'green',
    estadosRequeridos: ['COBRADO'],
    estadoNecesario: 'COBRADO',
    esFinal: true,
  },
];

// Formatear fecha corta
const formatFechaCorta = (fecha) => {
  if (!fecha) return '';
  return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
};

// Calcular días desde fecha
const diasDesde = (fecha) => {
  if (!fecha) return null;
  return Math.floor((new Date() - new Date(fecha)) / (1000 * 60 * 60 * 24));
};

export default function WorkflowVisual({ 
  cobretti, 
  contactos = [],
  onAccion
}) {
  // Determinar el paso actual basado en el estado y contactos
  const calcularPasoActual = () => {
    if (cobretti.estado === 'COBRADO') return 6;
    if (cobretti.estado === 'FINALIZADO' || cobretti.estado === 'CANCELADO') return 6;
    if (cobretti.estado === 'DEMANDA_EJECUCION') return 5;
    if (cobretti.estado === 'EXTRAJUDICIAL') return 4;
    
    // Contar contactos de reclamación
    const numContactos = contactos.length;
    if (numContactos >= 3) return 3; // Esperando decisión de escalar
    if (numContactos >= 2) return 2;
    if (numContactos >= 1) return 1;
    
    return 0; // Registro creado, sin contactos
  };

  const pasoActual = calcularPasoActual();
  const ultimoContacto = contactos.length > 0 
    ? contactos.reduce((a, b) => new Date(a.fecha_contacto) > new Date(b.fecha_contacto) ? a : b)
    : null;
  const diasUltimoContacto = ultimoContacto ? diasDesde(ultimoContacto.fecha_contacto) : null;

  // Determinar estado de cada paso
  const getEstadoPaso = (paso, index) => {
    if (cobretti.estado === 'CANCELADO') {
      return index <= pasoActual ? 'cancelado' : 'pendiente';
    }
    
    if (index < pasoActual) return 'completado';
    if (index === pasoActual) return 'actual';
    return 'pendiente';
  };

  // Obtener fecha de completado de un paso
  const getFechaCompletado = (paso, index) => {
    if (index === 0) return cobretti.fecha_creacion;
    if (paso.contactoRequerido && contactos[paso.contactoRequerido - 1]) {
      return contactos[paso.contactoRequerido - 1].fecha_contacto;
    }
    if (paso.estadoNecesario === cobretti.estado) {
      return cobretti.fecha_cambio_estado;
    }
    return null;
  };

  // Determinar si puede avanzar al siguiente paso
  const puedeAvanzar = () => {
    if (cobretti.estado === 'CANCELADO' || cobretti.estado === 'COBRADO' || cobretti.estado === 'FINALIZADO') {
      return false;
    }
    
    const pasoConfig = WORKFLOW_COMPLETO[pasoActual];
    if (!pasoConfig?.diasEspera) return true;
    
    return diasUltimoContacto !== null && diasUltimoContacto >= pasoConfig.diasEspera;
  };

  // Obtener acción del siguiente paso
  const getSiguienteAccion = () => {
    const siguientePaso = WORKFLOW_COMPLETO[pasoActual + 1];
    if (!siguientePaso) return null;
    
    if (siguientePaso.id.startsWith('RECLAMACION')) {
      return { tipo: 'email', plantilla: siguientePaso.plantilla, nombre: siguientePaso.nombre };
    }
    if (siguientePaso.id === 'EXTRAJUDICIAL') {
      return { tipo: 'estado', estado: 'EXTRAJUDICIAL', nombre: 'Escalar a Extrajudicial' };
    }
    if (siguientePaso.id === 'DEMANDA') {
      return { tipo: 'estado', estado: 'DEMANDA_EJECUCION', nombre: 'Iniciar Demanda' };
    }
    if (siguientePaso.id === 'COBRADO') {
      return { tipo: 'estado', estado: 'COBRADO', nombre: 'Marcar como Cobrado' };
    }
    return null;
  };

  const siguienteAccion = getSiguienteAccion();
  const pasoActualConfig = WORKFLOW_COMPLETO[pasoActual];

  // Obtener acciones disponibles para el paso actual
  const getAccionesDisponibles = () => {
    // "Tramitar Fase" solo disponible en estados EXTRAJUDICIAL o DEMANDA_EJECUCION
    if (cobretti.estado === 'EXTRAJUDICIAL' || cobretti.estado === 'DEMANDA_EJECUCION') {
      return pasoActualConfig?.accionesDisponibles || ['tramitar', 'email', 'telefono'];
    }
    // Para estados de reclamación, solo email, whatsapp y teléfono
    return ['email', 'whatsapp', 'telefono'];
  };

  const accionesDisponibles = getAccionesDisponibles();

  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white">
      <CardContent className="p-6">
        {/* Título */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-800">Flujo de Reclamación</h3>
          <span className="text-sm text-gray-500">
            Paso {pasoActual + 1} de {WORKFLOW_COMPLETO.length}
          </span>
        </div>

        {/* Flujo Visual Horizontal */}
        <div className="relative">
          {/* Línea de conexión */}
          <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 z-0" />
          <div 
            className="absolute top-8 left-0 h-1 bg-blue-500 z-0 transition-all duration-500"
            style={{ width: `${(pasoActual / (WORKFLOW_COMPLETO.length - 1)) * 100}%` }}
          />

          {/* Pasos */}
          <div className="relative z-10 flex justify-between">
            {WORKFLOW_COMPLETO.map((paso, index) => {
              const estado = getEstadoPaso(paso, index);
              const fecha = getFechaCompletado(paso, index);
              const Icono = paso.icono;
              
              return (
                <div key={paso.id} className="flex flex-col items-center" style={{ width: `${100 / WORKFLOW_COMPLETO.length}%` }}>
                  {/* Círculo del paso */}
                  <div className={`
                    w-16 h-16 rounded-full flex items-center justify-center border-4 transition-all
                    ${estado === 'completado' ? 'bg-green-500 border-green-500 text-white' : ''}
                    ${estado === 'actual' ? 'bg-blue-500 border-blue-500 text-white ring-4 ring-blue-200 animate-pulse' : ''}
                    ${estado === 'pendiente' ? 'bg-white border-gray-300 text-gray-400' : ''}
                    ${estado === 'cancelado' ? 'bg-gray-400 border-gray-400 text-white' : ''}
                  `}>
                    {estado === 'completado' ? (
                      <CheckCircle2 className="h-8 w-8" />
                    ) : estado === 'cancelado' ? (
                      <Ban className="h-6 w-6" />
                    ) : (
                      <Icono className="h-6 w-6" />
                    )}
                  </div>
                  
                  {/* Nombre del paso */}
                  <div className="mt-3 text-center">
                    <p className={`text-sm font-medium ${
                      estado === 'actual' ? 'text-blue-700' : 
                      estado === 'completado' ? 'text-green-700' : 
                      'text-gray-500'
                    }`}>
                      {paso.nombre}
                    </p>
                    {fecha && estado === 'completado' && (
                      <p className="text-xs text-gray-400">{formatFechaCorta(fecha)}</p>
                    )}
                    {estado === 'actual' && paso.diasEspera && diasUltimoContacto !== null && (
                      <p className="text-xs text-blue-600">
                        {diasUltimoContacto < paso.diasEspera 
                          ? `Esperar ${paso.diasEspera - diasUltimoContacto}d` 
                          : '¡Listo!'}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Panel de Acción Actual */}
        {cobretti.estado !== 'CANCELADO' && cobretti.estado !== 'COBRADO' && cobretti.estado !== 'FINALIZADO' && (
          <div className={`mt-8 p-4 rounded-lg border-2 ${
            puedeAvanzar() ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                {puedeAvanzar() ? (
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                    <ArrowRight className="h-5 w-5 text-white" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
                    <Pause className="h-5 w-5 text-white" />
                  </div>
                )}
                <div>
                  <p className="font-bold text-gray-800">
                    {puedeAvanzar() ? '¡Puede avanzar!' : 'En espera'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {puedeAvanzar() 
                      ? `Proceda con: ${siguienteAccion?.nombre || 'siguiente paso'}`
                      : `Esperando ${pasoActualConfig?.diasEspera - diasUltimoContacto} días más`
                    }
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {/* Acción principal de avance */}
                {puedeAvanzar() && siguienteAccion && (
                  <>
                    {siguienteAccion.tipo === 'email' && (
                      <Button onClick={() => onAccion('email', siguienteAccion.plantilla)} className="bg-blue-600 hover:bg-blue-700">
                        <Mail className="h-4 w-4 mr-2" />
                        {siguienteAccion.nombre}
                      </Button>
                    )}
                    {siguienteAccion.tipo === 'estado' && (
                      <Button 
                        onClick={() => onAccion('estado', siguienteAccion.estado)}
                        className={
                          siguienteAccion.estado === 'COBRADO' ? 'bg-green-600 hover:bg-green-700' :
                          siguienteAccion.estado === 'EXTRAJUDICIAL' ? 'bg-orange-600 hover:bg-orange-700' :
                          siguienteAccion.estado === 'DEMANDA_EJECUCION' ? 'bg-red-600 hover:bg-red-700' :
                          ''
                        }
                      >
                        {siguienteAccion.estado === 'EXTRAJUDICIAL' && <FileText className="h-4 w-4 mr-2" />}
                        {siguienteAccion.estado === 'DEMANDA_EJECUCION' && <Scale className="h-4 w-4 mr-2" />}
                        {siguienteAccion.estado === 'COBRADO' && <Euro className="h-4 w-4 mr-2" />}
                        {siguienteAccion.nombre}
                      </Button>
                    )}
                  </>
                )}
                
                {/* Acciones alternativas siempre disponibles */}
                <div className="flex gap-2 border-l pl-2 ml-2 border-gray-300">
                  {/* Email - siempre disponible en reclamaciones */}
                  {accionesDisponibles.includes('email') && !puedeAvanzar() && (
                    <Button variant="outline" onClick={() => onAccion('email')} className="border-blue-300 text-blue-700 hover:bg-blue-50">
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </Button>
                  )}
                  
                  {/* WhatsApp */}
                  {accionesDisponibles.includes('whatsapp') && (
                    <Button variant="outline" onClick={() => onAccion('whatsapp')} className="border-green-300 text-green-700 hover:bg-green-50">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp
                    </Button>
                  )}
                  
                  {/* Teléfono */}
                  <Button variant="outline" onClick={() => onAccion('telefono')}>
                    <Phone className="h-4 w-4 mr-2" />
                    Llamar
                  </Button>
                  
                  {/* Tramitar fase (para Extrajudicial y Demanda) */}
                  {accionesDisponibles.includes('tramitar') && (
                    <Button variant="outline" onClick={() => onAccion('tramitar')} className="border-purple-300 text-purple-700 hover:bg-purple-50">
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Tramitar Fase
                    </Button>
                  )}
                </div>
                
                {/* Botón escalar (después de 3 reclamaciones) */}
                {pasoActual >= 3 && cobretti.estado === 'EN_RECLAMACION' && (
                  <Button 
                    variant="outline" 
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    onClick={() => onAccion('estado', 'EXTRAJUDICIAL')}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Escalar
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Estado Final */}
        {(cobretti.estado === 'COBRADO' || cobretti.estado === 'FINALIZADO') && (
          <>
            {/* Si está en COBRADO pero hay saldo pendiente, mostrar formulario de pago */}
            {cobretti.estado === 'COBRADO' && (cobretti.importe_total - (cobretti.importe_cobrado || 0)) > 0 ? (
              <div className="mt-8 p-4 rounded-lg bg-yellow-50 border-2 border-yellow-400">
                <div className="text-center mb-4">
                  <Euro className="h-10 w-10 text-yellow-600 mx-auto mb-2" />
                  <p className="font-bold text-yellow-800 text-lg">Pendiente de Cobro</p>
                  <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Total:</span>
                      <p className="font-semibold">{cobretti.importe_total?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Cobrado:</span>
                      <p className="font-semibold text-green-600">{(cobretti.importe_cobrado || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Pendiente:</span>
                      <p className="font-bold text-red-600">{(cobretti.importe_total - (cobretti.importe_cobrado || 0)).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Button
                    onClick={() => onAccion?.({ tipo: 'registrar_pago' })}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    Registrar Pago
                  </Button>
                </div>
              </div>
            ) : (
              /* Deuda completamente cobrada */
              <div className="mt-8 p-4 rounded-lg bg-green-100 border-2 border-green-400 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-2" />
                <p className="font-bold text-green-800 text-lg">
                  {cobretti.estado === 'COBRADO' ? '¡Deuda Cobrada Completamente!' : 'Proceso Finalizado'}
                </p>
                {cobretti.estado === 'COBRADO' && (
                  <>
                    <p className="text-green-600">
                      Importe recuperado: {cobretti.importe_cobrado?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                    </p>
                    <Button
                      onClick={() => onAccion?.({ tipo: 'finalizar_expediente' })}
                      className="mt-4 bg-blue-600 hover:bg-blue-700"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Finalizar Expediente
                    </Button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {cobretti.estado === 'CANCELADO' && (
          <div className="mt-8 p-4 rounded-lg bg-gray-100 border-2 border-gray-400 text-center">
            <Ban className="h-12 w-12 text-gray-600 mx-auto mb-2" />
            <p className="font-bold text-gray-800 text-lg">Proceso Cancelado</p>
            {cobretti.motivo_cancelacion && (
              <p className="text-gray-600 text-sm mt-1">{cobretti.motivo_cancelacion}</p>
            )}
          </div>
        )}

        {/* Leyenda */}
        <div className="mt-6 flex justify-center gap-6 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500" /> Completado
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" /> Actual
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-300" /> Pendiente
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
